import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, MessageCircle, Users, Store } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { ForgedWheel } from "@/components/Logo";

/**
 * Phone-only bottom nav. Sharp, angular, brutalist —
 * with the Garage as a raised center-tab (bigger red circle, more visual weight).
 * Hidden md+ (iPad + Desktop use <TopNavBar />).
 */

// Order matters — Garage sits at index 2 (center of 5).
// Home · Market · GARAGE · Inbox · Feed
const TABS = [
    { to: "/", label: "Home", icon: Home, testid: "tab-home" },
    { to: "/marketplace", label: "Market", icon: Store, testid: "tab-marketplace" },
    // index 2 → GARAGE (rendered specially below)
    { to: "/messages", label: "Inbox", icon: MessageCircle, testid: "tab-messages" },
    { to: "/community", label: "Feed", icon: Users, testid: "tab-community" },
];

export default function BottomTabBar({ onFabClick }) {
    const { user } = useAuth();
    const navigate = useNavigate();

    // Split before/after the center Garage slot
    const leftTabs = TABS.slice(0, 2);
    const rightTabs = TABS.slice(2);

    return (
        <nav
            className="md:hidden fixed left-0 right-0 bottom-0 z-40 safe-bottom"
            style={{
                background: "rgba(6,6,6,0.94)",
                borderTop: "1px solid #262626",
                backdropFilter: "blur(18px)",
                WebkitBackdropFilter: "blur(18px)",
                paddingBottom: "env(safe-area-inset-bottom, 0px)",
                boxShadow: "0 -12px 30px rgba(0,0,0,0.5)",
            }}
            data-testid="bottom-tab-bar"
        >
            <div className="flex items-stretch h-16 px-0 relative">
                {leftTabs.map((t) => (
                    <FlatTab key={t.to} tab={t} />
                ))}

                {/* Center: raised Garage circle */}
                <div className="flex-1 flex items-start justify-center relative">
                    <motion.button
                        data-testid="tab-garage"
                        onClick={() => {
                            if (!user) return navigate("/login");
                            navigate("/garage");
                        }}
                        whileTap={{ scale: 0.92 }}
                        whileHover={{ y: -2 }}
                        className="absolute flex flex-col items-center"
                        style={{
                            top: -22,
                            // isolate for filter/shadow
                            filter: "drop-shadow(0 6px 14px rgba(225,6,0,0.55))",
                        }}
                        aria-label="Garage"
                    >
                        <span
                            className="relative flex items-center justify-center"
                            style={{
                                width: 62,
                                height: 62,
                                borderRadius: "50%",
                                background:
                                    "radial-gradient(circle at 32% 28%, #ff5f56 0%, #E10600 55%, #7a0400 100%)",
                                border: "3px solid #0a0a0a",
                                boxShadow:
                                    "inset 0 2px 0 rgba(255,255,255,0.22), inset 0 -6px 12px rgba(0,0,0,0.35), 0 0 0 1.5px rgba(225,6,0,0.35)",
                            }}
                        >
                            <ForgedWheel size={40} spin />
                            {/* Little "ON" red heartbeat pulse ring */}
                            <motion.span
                                aria-hidden
                                className="absolute inset-0 rounded-full pointer-events-none"
                                initial={{ opacity: 0.55, scale: 1 }}
                                animate={{ opacity: [0.55, 0, 0.55], scale: [1, 1.35, 1] }}
                                transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }}
                                style={{
                                    border: "2px solid rgba(225,6,0,0.6)",
                                }}
                            />
                        </span>
                        <span
                            className="mt-1 text-[10px] font-bold text-white"
                            style={{ letterSpacing: "0.16em" }}
                        >
                            GARAGE
                        </span>
                    </motion.button>
                </div>

                {rightTabs.map((t) => (
                    <FlatTab key={t.to} tab={t} />
                ))}
            </div>
        </nav>
    );
}

function FlatTab({ tab }) {
    return (
        <NavLink
            to={tab.to}
            data-testid={tab.testid}
            end={tab.to === "/" || tab.to === "/marketplace"}
            className={({ isActive }) =>
                `relative flex-1 flex flex-col items-center justify-center gap-1 ${
                    isActive ? "text-white" : "text-onyx-500"
                }`
            }
            style={{ transition: "color 0.15s ease" }}
        >
            {({ isActive }) => (
                <>
                    {isActive && (
                        <motion.span
                            layoutId="tab-active-marker"
                            className="absolute top-0 left-2 right-2 h-[3px]"
                            style={{
                                background: "#E10600",
                                boxShadow: "0 0 12px rgba(225,6,0,0.6)",
                            }}
                            transition={{ type: "spring", stiffness: 380, damping: 34 }}
                        />
                    )}
                    <tab.icon
                        className="w-5 h-5"
                        strokeWidth={isActive ? 2.4 : 2}
                    />
                    <span
                        className="text-[10px] font-bold"
                        style={{ letterSpacing: "0.12em" }}
                    >
                        {tab.label}
                    </span>
                </>
            )}
        </NavLink>
    );
}
